/**
 * Solid-silhouette SVG → tinted data-URL pipeline (Q.1 Stage 4).
 *
 * Operational markers (aircraft airframes, fire trucks, base/hangar depots) are
 * authored as solid `fill="currentColor"` SVGs under `assets/`, imported as raw
 * strings (`?raw`). This module swaps `currentColor` for the role colour and
 * encodes the result as an `image/svg+xml` data-URL that Cesium billboards (and,
 * later, the R.1.5 hero strip) consume directly. SVGs scale cleanly, so the same
 * source renders crisp at a 22 px map marker and an 80 px hero glyph.
 *
 * Unlike the stroke-only Lucide canvas pipeline (`iconRegistry`), these are solid
 * silhouettes and need no canvas — the transform is pure string work, so it runs
 * (and is unit-tested) outside the browser. Results are cached by key+colour so
 * repeat renders at high marker density are free.
 */

const cache = new Map<string, string>();

/**
 * Tint a raw `currentColor` SVG and return a cached `image/svg+xml` data-URL.
 * `key` is a stable identifier for the source SVG (the registry key) so the
 * cache need not hash the markup. Idempotent for a given (key, color).
 */
export function tintedSvgDataUrl(key: string, rawSvg: string, color: string): string {
  const cacheKey = `${key}|${color}`;
  const hit = cache.get(cacheKey);
  if (hit !== undefined) return hit;
  const tinted = rawSvg.replaceAll('currentColor', color);
  // encodeURIComponent keeps `#` (colour hashes) and `<`/`>` safe in the URL.
  const url = `data:image/svg+xml,${encodeURIComponent(tinted)}`;
  cache.set(cacheKey, url);
  return url;
}

/** Current cache size. Test-only. */
export function __svgCacheSize(): number {
  return cache.size;
}

/** Clear the tint cache. Test-only. */
export function __clearSvgCache(): void {
  cache.clear();
}
